import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  Check, 
  X as XIcon, 
  AlertCircle, 
  Users, 
  Package, 
  LogOut, 
  BarChart3, 
  Building2, 
  Ship, 
  Layers, 
  ThumbsUp, 
  ThumbsDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download
} from 'lucide-react';
import solicitudService from '../services/solicitudService';
import equipoService from '../services/equipoService';
import tipoEquipoService from '../services/tipoEquipoService';
import { authService } from '../services/authService';

function Solicitudes() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [solicitudes, setSolicitudes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [tiposEquipo, setTiposEquipo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  
  const [revisionData, setRevisionData] = useState({
    aprobada: null,
    comentario: ''
  });
  const [success, setSuccess] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [exportando, setExportando] = useState(false);

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];
  const anioActual = new Date().getFullYear();
  const anios = Array.from({ length: 6 }, (_, i) => anioActual - i);

  // Estados de paginación
  const [paginacion, setPaginacion] = useState({
    total: 0,
    skip: 0,
    limit: 25,
    total_pages: 0
  });
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(25);

  useEffect(() => {
    loadData();
  }, [filtroEstado, filtroMes, filtroAnio, paginaActual, registrosPorPagina]);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const skip = (paginaActual - 1) * registrosPorPagina;
      const limit = registrosPorPagina;
      
      const params = {
        skip: skip,
        limit: limit
      };
      
      if (filtroEstado) {
        params.estado = filtroEstado;
      }
      if (filtroMes) {
        params.mes = filtroMes;
      }
      if (filtroAnio) {
        params.anio = filtroAnio;
      }

      const [solicitudesResponse, equiposData, tiposData] = await Promise.all([
        solicitudService.getSolicitudes(params),
        equipoService.getEquipos({ skip: 0, limit: 10000 }),
        tipoEquipoService.getTiposEquipo()
      ]);
      
      // Manejar respuesta con paginación
      setSolicitudes(solicitudesResponse.solicitudes || solicitudesResponse);
      setPaginacion({
        total: solicitudesResponse.total || 0,
        skip: solicitudesResponse.skip || 0,
        limit: solicitudesResponse.limit || limit,
        total_pages: solicitudesResponse.total_pages || 1
      });
      
      setEquipos(equiposData.equipos || equiposData);
      setTiposEquipo(tiposData);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarPagina = (nuevaPagina) => {
    if (nuevaPagina >= 1 && nuevaPagina <= paginacion.total_pages) {
      setPaginaActual(nuevaPagina);
    }
  };

  const handleCambiarRegistrosPorPagina = (cantidad) => {
    setRegistrosPorPagina(parseInt(cantidad));
    setPaginaActual(1);
  };

  const obtenerRangoMostrado = () => {
    if (paginacion.total === 0) return '0-0 de 0';
    const inicio = paginacion.skip + 1;
    const fin = Math.min(paginacion.skip + solicitudes.length, paginacion.total);
    return `${inicio}-${fin} de ${paginacion.total}`;
  };

  const generarNumerosPagina = () => {
    const paginas = [];
    const totalPaginas = paginacion.total_pages;
    const actual = paginaActual;
    
    if (totalPaginas <= 7) {
      for (let i = 1; i <= totalPaginas; i++) {
        paginas.push(i);
      }
    } else {
      if (actual <= 4) {
        for (let i = 1; i <= 5; i++) paginas.push(i);
        paginas.push('...');
        paginas.push(totalPaginas);
      } else if (actual >= totalPaginas - 3) {
        paginas.push(1);
        paginas.push('...');
        for (let i = totalPaginas - 4; i <= totalPaginas; i++) paginas.push(i);
      } else {
        paginas.push(1);
        paginas.push('...');
        for (let i = actual - 1; i <= actual + 1; i++) paginas.push(i);
        paginas.push('...');
        paginas.push(totalPaginas);
      }
    }
    
    return paginas;
  };

  const handleExportar = async () => {
    try {
      setExportando(true);
      setError('');
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroMes) params.mes = filtroMes;
      if (filtroAnio) params.anio = filtroAnio;

      const blob = await solicitudService.exportarExcel(params);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const mesLabel = filtroMes ? meses.find(m => m.value === parseInt(filtroMes))?.label : '';
      const sufijo = (filtroMes && filtroAnio)
        ? `${mesLabel}_${filtroAnio}`
        : (filtroAnio || 'todas');
      link.setAttribute('download', `Solicitudes_${sufijo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Error al exportar solicitudes');
    } finally {
      setExportando(false);
    }
  };

  const handleOpenRevisionModal = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setRevisionData({
      aprobada: null,
      comentario: ''
    });
    setShowRevisionModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowRevisionModal(false);
    setSelectedSolicitud(null);
    setError('');
    setSuccess('');
  };

  const handleRevisar = async (aprobada) => {
    setError('');
    setSuccess('');

    try {
      await solicitudService.revisarSolicitud(
        selectedSolicitud.id,
        aprobada,
        revisionData.comentario || null
      );
      setSuccess(`Solicitud ${aprobada ? 'aprobada' : 'rechazada'} exitosamente`);
      await loadData();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setError(err);
    }
  };

  const handleDelete = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta solicitud?')) {
      return;
    }

    try {
      setError('');
      await solicitudService.deleteSolicitud(solicitudId);
      setSuccess('Solicitud eliminada exitosamente');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err);
    }
  };

  const handleRevisionInputChange = (e) => {
    const { name, value } = e.target;
    setRevisionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getEstadoSolicitudBadge = (estado) => {
    const badges = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800'
    };
    const labels = {
      pendiente: 'Pendiente',
      aprobada: 'Aprobada',
      rechazada: 'Rechazada'
    };
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badges[estado]}`}>
        {labels[estado]}
      </span>
    );
  };

  const getEstadoEquipoBadge = (estado) => {
    const badges = {
      activo: 'bg-green-100 text-green-800',
      inactivo: 'bg-gray-100 text-gray-800',
      suspendido: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      activo: 'Activo',
      inactivo: 'Inactivo',
      suspendido: 'Suspendido'
    };
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badges[estado]}`}>
        {labels[estado]}
      </span>
    );
  };

  const getTipoSolicitudLabel = (tipo) => {
    return tipo === 'cambio_estado' ? 'Cambio de Estado' : 'Registro de Equipo';
  };

  const getEquipoInfo = (equipoId) => {
    const equipo = equipos.find(e => e.id === equipoId);
    return equipo ? `${equipo.numero_serie} - ${equipo.embarcacion}` : 'N/A';
  };

  const getTipoEquipoNombre = (tipoId) => {
    const tipo = tiposEquipo.find(t => t.id === tipoId);
    return tipo ? tipo.nombre : 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      let utcDateString = dateString;
      
      if (dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
        utcDateString = dateString + 'Z';
      }
      
      const date = new Date(utcDateString);
      
      return date.toLocaleString('es', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error al convertir fecha:', error);
      return dateString;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/equipos" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <Package size={20} />
            <span>Equipos</span>
          </Link>
          <Link to="/tipos-equipo" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>
          {user?.rol === 'admin' && (
            <Link to="/clientes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Building2 size={20} />
              <span>Clientes</span>
            </Link>
          )}
          {user?.rol === 'admin' && (
            <Link to="/users" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}
          <Link to="/solicitudes" className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg">
            <FileText size={20} />
            <span>Solicitudes</span>
          </Link>
          <Link to="/informes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-blue-800 rounded-lg transition" title="Cerrar Sesión">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white shadow-sm p-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-800">Solicitudes</h1>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filtroMes}
                onChange={(e) => { setFiltroMes(e.target.value); setPaginaActual(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Todos los meses</option>
                {meses.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={filtroAnio}
                onChange={(e) => { setFiltroAnio(e.target.value); setPaginaActual(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Todos los años</option>
                {anios.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <select
                value={filtroEstado}
                onChange={(e) => { setFiltroEstado(e.target.value); setPaginaActual(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Todas</option>
                <option value="pendiente">Pendientes</option>
                <option value="aprobada">Aprobadas</option>
                <option value="rechazada">Rechazadas</option>
              </select>
              <button
                onClick={handleExportar}
                disabled={exportando || solicitudes.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar a Excel"
              >
                <Download size={18} />
                {exportando ? 'Exportando...' : 'Exportar'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">Cargando solicitudes...</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {solicitudes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          No hay solicitudes registradas
                        </td>
                      </tr>
                    ) : (
                      solicitudes.map((solicitud) => (
                        <tr key={solicitud.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-900">
                              {getTipoSolicitudLabel(solicitud.tipo_solicitud)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {solicitud.tipo_solicitud === 'cambio_estado' ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {getEquipoInfo(solicitud.equipo_id)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Estado: {getEstadoEquipoBadge(solicitud.estado_solicitado)}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {solicitud.numero_serie}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {solicitud.embarcacion} - {getTipoEquipoNombre(solicitud.tipo_equipo_id)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Estado inicial:{' '}
                                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Activo
                                  </span>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {solicitud.motivo}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {getEstadoSolicitudBadge(solicitud.estado_solicitud)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(solicitud.created_at)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-medium">
                            {solicitud.estado_solicitud === 'pendiente' && (
                              <div className="flex justify-end gap-2">
                                {user?.rol === 'admin' ? (
                                  <button
                                    onClick={() => handleOpenRevisionModal(solicitud)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Revisar"
                                  >
                                    <Check size={18} />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleDelete(solicitud.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Cancelar solicitud"
                                  >
                                    <XIcon size={18} />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Controles de Paginación */}
              {solicitudes.length > 0 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Información de registros */}
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{obtenerRangoMostrado()}</span> solicitudes
                      </p>
                      
                      {/* Selector de cantidad por página */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">Mostrar:</label>
                        <select
                          value={registrosPorPagina}
                          onChange={(e) => handleCambiarRegistrosPorPagina(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={500}>500</option>
                        </select>
                      </div>
                    </div>

                    {/* Botones de navegación */}
                    {paginacion.total_pages > 1 && (
                      <div className="flex items-center gap-2">
                        {/* Primera página */}
                        <button
                          onClick={() => handleCambiarPagina(1)}
                          disabled={paginaActual === 1}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Primera página"
                        >
                          <ChevronsLeft size={18} />
                        </button>

                        {/* Página anterior */}
                        <button
                          onClick={() => handleCambiarPagina(paginaActual - 1)}
                          disabled={paginaActual === 1}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Anterior"
                        >
                          <ChevronLeft size={18} />
                        </button>

                        {/* Números de página */}
                        <div className="flex items-center gap-1">
                          {generarNumerosPagina().map((num, idx) => (
                            num === '...' ? (
                              <span key={`dots-${idx}`} className="px-3 py-1 text-gray-500">...</span>
                            ) : (
                              <button
                                key={num}
                                onClick={() => handleCambiarPagina(num)}
                                className={`px-3 py-1 rounded-lg border ${
                                  paginaActual === num
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {num}
                              </button>
                            )
                          ))}
                        </div>

                        {/* Página siguiente */}
                        <button
                          onClick={() => handleCambiarPagina(paginaActual + 1)}
                          disabled={paginaActual === paginacion.total_pages}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Siguiente"
                        >
                          <ChevronRight size={18} />
                        </button>

                        {/* Última página */}
                        <button
                          onClick={() => handleCambiarPagina(paginacion.total_pages)}
                          disabled={paginaActual === paginacion.total_pages}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Última página"
                        >
                          <ChevronsRight size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Revisar Solicitud */}
      {showRevisionModal && selectedSolicitud && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Revisar Solicitud</h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <XIcon size={24} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {success}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <p className="text-gray-900">{getTipoSolicitudLabel(selectedSolicitud.tipo_solicitud)}</p>
              </div>

              {selectedSolicitud.tipo_solicitud === 'cambio_estado' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Equipo</label>
                    <p className="text-gray-900">{getEquipoInfo(selectedSolicitud.equipo_id)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado Solicitado</label>
                    <div>{getEstadoEquipoBadge(selectedSolicitud.estado_solicitado)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Serie</label>
                    <p className="text-gray-900">{selectedSolicitud.numero_serie}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Embarcación</label>
                    <p className="text-gray-900">{selectedSolicitud.embarcacion}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Equipo</label>
                    <p className="text-gray-900">{getTipoEquipoNombre(selectedSolicitud.tipo_equipo_id)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado Inicial</label>
                    <div>{getEstadoEquipoBadge('activo')}</div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <p className="text-gray-900 text-sm">{selectedSolicitud.motivo}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comentario (opcional)</label>
                <textarea
                  name="comentario"
                  value={revisionData.comentario}
                  onChange={handleRevisionInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Agrega un comentario sobre tu decisión"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRevisar(false)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <ThumbsDown size={18} />
                Rechazar
              </button>
              <button
                onClick={() => handleRevisar(true)}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <ThumbsUp size={18} />
                Aprobar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Solicitudes;